import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Users, Plus, UserPlus, Check, X, AlertTriangle, 
  ArrowLeft, Shield, FileText, ChevronRight, Baby,
  BookOpen, Languages, Building2, Palette, Dumbbell, TreePine, Atom
} from "lucide-react";
import BottomNav from "@/components/BottomNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category icons mapping
const CATEGORY_ICONS = {
  babysitting: Baby,
  tutoring: BookOpen,
  language: Languages,
  educational_trips: Building2,
  quirky_activities: Palette,
  sports: Dumbbell,
  nature: TreePine,
  stem: Atom,
};

const BuddyUp = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pools, setPools] = useState([]);
  const [matches, setMatches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [terms, setTerms] = useState(null);
  const [legalStatus, setLegalStatus] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [filterCategory, setFilterCategory] = useState("");
  const [newPool, setNewPool] = useState({ 
    name: "", 
    description: "", 
    category: "babysitting",
    invited_user_ids: [] 
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [poolsRes, matchesRes, legalRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/sitter-pools`),
        axios.get(`${API}/matches`),
        axios.get(`${API}/legal/status`),
        axios.get(`${API}/buddy-up/categories`)
      ]);
      setPools(poolsRes.data.pools || []);
      setMatches(matchesRes.data.matches || []);
      setLegalStatus(legalRes.data.status || {});
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTerms = async () => {
    try {
      const res = await axios.get(`${API}/legal/sitter-pool-terms`);
      setTerms(res.data);
      setShowTerms(true);
    } catch (error) {
      toast.error("Failed to load terms");
    }
  };

  const acceptTerms = async () => {
    try {
      await axios.post(`${API}/legal/accept`, { feature: "sitter_pool" });
      toast.success("Terms accepted!");
      setShowTerms(false);
      setLegalStatus(prev => ({
        ...prev,
        sitter_pool: { accepted: true, is_current_version: true }
      }));
    } catch (error) {
      toast.error("Failed to accept terms");
    }
  };

  const createPool = async () => {
    if (!legalStatus?.sitter_pool?.accepted || !legalStatus?.sitter_pool?.is_current_version) {
      fetchTerms();
      return;
    }

    if (!newPool.name.trim()) {
      toast.error("Please enter a pool name");
      return;
    }

    try {
      await axios.post(`${API}/sitter-pools`, newPool);
      toast.success("Buddy UP! pool created!");
      setShowCreate(false);
      setSelectedCategory(null);
      setNewPool({ name: "", description: "", category: "babysitting", invited_user_ids: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create pool");
    }
  };

  const joinPool = async (poolId) => {
    if (!legalStatus?.sitter_pool?.accepted || !legalStatus?.sitter_pool?.is_current_version) {
      fetchTerms();
      return;
    }

    try {
      await axios.post(`${API}/sitter-pools/${poolId}/join`);
      toast.success("You've joined the pool!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join pool");
    }
  };

  const toggleInvite = (userId) => {
    setNewPool(prev => ({
      ...prev,
      invited_user_ids: prev.invited_user_ids.includes(userId)
        ? prev.invited_user_ids.filter(id => id !== userId)
        : [...prev.invited_user_ids, userId]
    }));
  };

  const selectCategory = (category) => {
    setSelectedCategory(category);
    setNewPool(prev => ({ ...prev, category: category.id }));
  };

  const hasAcceptedTerms = legalStatus?.sitter_pool?.accepted && legalStatus?.sitter_pool?.is_current_version;

  const filteredPools = filterCategory 
    ? pools.filter(p => p.category === filterCategory)
    : pools;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF9F5] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9F5] pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 via-teal-600 to-purple-500 text-white p-6 pt-8">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/dashboard" className="p-2 -ml-2 hover:bg-white/10 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Buddy UP!
            </h1>
            <p className="text-white/80 text-sm">
              Team up with families to share activities, clubs & costs
            </p>
          </div>
        </div>
      </div>

      {/* Category Filter Pills */}
      <div className="px-4 py-3 bg-white border-b border-slate-100 overflow-x-auto">
        <div className="flex gap-2">
          <button
            onClick={() => setFilterCategory("")}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              !filterCategory 
                ? 'bg-teal-500 text-white' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            All
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setFilterCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${
                filterCategory === cat.id 
                  ? 'bg-teal-500 text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <span>{cat.icon}</span>
              <span>{cat.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Legal Notice Banner */}
      {!hasAcceptedTerms && (
        <div className="mx-4 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-amber-800 font-medium text-sm">Terms Required</p>
              <p className="text-amber-600 text-xs mt-1">
                Please read and accept our terms before creating or joining a Buddy UP! pool.
              </p>
              <Button 
                onClick={fetchTerms}
                size="sm"
                className="mt-2 bg-amber-500 hover:bg-amber-600 text-white"
              >
                <FileText className="w-4 h-4 mr-1" />
                View Terms
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Pool Button */}
      <div className="p-4">
        <Button
          onClick={() => setShowCreate(true)}
          className="w-full bg-gradient-to-r from-teal-500 to-purple-500 hover:from-teal-600 hover:to-purple-600 text-white rounded-xl py-6"
          data-testid="create-buddyup-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Create New Buddy UP! Pool
        </Button>
      </div>

      {/* Pools List */}
      <div className="px-4 space-y-4">
        {filteredPools.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No pools yet</p>
            <p className="text-slate-400 text-sm mt-1">
              Create one to start sharing costs with matched families!
            </p>
          </div>
        ) : (
          filteredPools.map(pool => {
            const isInvited = pool.invited?.some(i => i.user_id === user?.user_id);
            const isMember = pool.members?.some(m => m.user_id === user?.user_id);
            const CategoryIcon = CATEGORY_ICONS[pool.category] || Users;
            
            return (
              <div 
                key={pool.pool_id} 
                className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-100 to-purple-100 flex items-center justify-center text-2xl">
                    {pool.category_icon || <CategoryIcon className="w-6 h-6 text-teal-600" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-800">{pool.name}</h3>
                      {pool.category_name && (
                        <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">
                          {pool.category_name}
                        </span>
                      )}
                    </div>
                    {pool.description && (
                      <p className="text-sm text-slate-500 mt-1">{pool.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {pool.members?.length || 0} members
                      </span>
                      {pool.invited?.length > 0 && (
                        <span>{pool.invited.length} pending</span>
                      )}
                    </div>
                  </div>
                  
                  {isInvited && !isMember ? (
                    <Button 
                      onClick={() => joinPool(pool.pool_id)}
                      size="sm"
                      className="bg-teal-500 hover:bg-teal-600"
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Join
                    </Button>
                  ) : isMember ? (
                    <Link to={`/sitter-pools/${pool.pool_id}`}>
                      <Button size="sm" variant="outline" className="border-teal-300 text-teal-600">
                        View
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Create Pool Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-slate-800">Create Buddy UP! Pool</h2>
                <button 
                  onClick={() => { setShowCreate(false); setSelectedCategory(null); }}
                  className="p-2 hover:bg-slate-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Step 1: Choose Category */}
              {!selectedCategory ? (
                <div className="space-y-3">
                  <p className="text-slate-600 mb-4">What would you like to Buddy UP! on?</p>
                  {categories.map(cat => {
                    const CategoryIcon = CATEGORY_ICONS[cat.id] || Users;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => selectCategory(cat)}
                        className="w-full p-4 bg-slate-50 hover:bg-teal-50 border border-slate-200 hover:border-teal-300 rounded-xl text-left transition-colors flex items-center gap-4"
                        data-testid={`category-${cat.id}`}
                      >
                        <span className="text-3xl">{cat.icon}</span>
                        <div>
                          <h3 className="font-semibold text-slate-800">{cat.name}</h3>
                          <p className="text-sm text-slate-500">{cat.description}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 ml-auto" />
                      </button>
                    );
                  })}
                </div>
              ) : (
                /* Step 2: Pool Details */
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-teal-50 rounded-xl mb-4">
                    <span className="text-2xl">{selectedCategory.icon}</span>
                    <div>
                      <p className="font-medium text-teal-800">{selectedCategory.name}</p>
                      <button 
                        onClick={() => setSelectedCategory(null)}
                        className="text-xs text-teal-600 hover:underline"
                      >
                        Change category
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-700">Pool Name</Label>
                    <Input
                      value={newPool.name}
                      onChange={(e) => setNewPool(p => ({ ...p, name: e.target.value }))}
                      placeholder={`e.g., "Weekend ${selectedCategory.name}"`}
                      className="mt-1"
                      data-testid="pool-name-input"
                    />
                  </div>

                  <div>
                    <Label className="text-slate-700">Description (optional)</Label>
                    <textarea
                      value={newPool.description}
                      onChange={(e) => setNewPool(p => ({ ...p, description: e.target.value }))}
                      placeholder="What are you looking to do together?"
                      className="w-full mt-1 p-3 border border-slate-200 rounded-xl resize-none h-20 focus:outline-none focus:ring-2 focus:ring-teal-500"
                      data-testid="pool-description-input"
                    />
                  </div>

                  {/* Invite Matches */}
                  <div>
                    <Label className="text-slate-700 mb-2 block">Invite Matched Families</Label>
                    {matches.length === 0 ? (
                      <p className="text-sm text-slate-500 bg-slate-50 p-3 rounded-xl">
                        No matches yet. Match with families first to invite them!
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {matches.map(match => (
                          <button
                            key={match.user_id || match.target_user_id}
                            onClick={() => toggleInvite(match.target_user_id)}
                            className={`w-full p-3 rounded-xl flex items-center gap-3 transition-colors ${
                              newPool.invited_user_ids.includes(match.target_user_id)
                                ? 'bg-teal-100 border-2 border-teal-500'
                                : 'bg-slate-50 border border-slate-200 hover:bg-slate-100'
                            }`}
                          >
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-purple-400 flex items-center justify-center text-white font-bold">
                              {match.target_name?.[0] || '?'}
                            </div>
                            <span className="font-medium text-slate-700">{match.target_name || 'Family'}</span>
                            {newPool.invited_user_ids.includes(match.target_user_id) && (
                              <Check className="w-5 h-5 text-teal-600 ml-auto" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button 
                      onClick={() => setSelectedCategory(null)} 
                      variant="outline" 
                      className="flex-1"
                    >
                      Back
                    </Button>
                    <Button 
                      onClick={createPool}
                      className="flex-1 bg-teal-500 hover:bg-teal-600"
                      data-testid="submit-pool-btn"
                    >
                      Create Pool
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Terms Modal */}
      {showTerms && terms && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-800">Terms & Conditions</h2>
              <button onClick={() => setShowTerms(false)} className="p-2 hover:bg-slate-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="prose prose-sm max-w-none">
                <pre className="whitespace-pre-wrap text-xs text-slate-600 font-sans">
                  {terms.terms}
                </pre>
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex gap-3">
              <Button onClick={() => setShowTerms(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button onClick={acceptTerms} className="flex-1 bg-teal-500 hover:bg-teal-600">
                <Check className="w-4 h-4 mr-2" />
                Accept Terms
              </Button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default BuddyUp;
